import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  createTheme,
  ThemeProvider,
  Box,
} from "@mui/material";

import { formatDistanceToNow } from "date-fns";
import { zhCN, zhHK } from "date-fns/locale";
import useSort from "../../utils/useSort";
import { useTranslation } from "react-i18next";
import { KeyboardArrowLeft, KeyboardArrowRight, Link } from "@mui/icons-material";
import useMapHeight from "./useMapHeight";
import truncateText from "../../utils/truncateText";

const theme = createTheme();

const getCellSx = (accessor) => {
  if (["recorded_at", "code", "type"].includes(accessor)) {
    return {
      width: {
        xs: "2%",
        sm: "3%",
        md: "4%",
        lg: "5%",
        xl: "6%",
      },
      padding: "2px 4px",
    };
  }
  return {
    width: {
      xs: "8%",
      sm: "11%",
      md: "14%",
      lg: "17%",
      xl: "20%",
    },
    padding: "2px 4px",
  };
};

const LinkToProjectButton = ({ id, handleButtonOnClick, t }) => {
  if (id !== null) {
    return (
      <IconButton size='small' onClick={() => handleButtonOnClick()}>
        <Tooltip title={t("gotoproject")}>
          <Link />
        </Tooltip>
      </IconButton>
    );
  }
  return <></>;
};

const ActivityListComponent = ({ state, setState, setProjectState, activities, fetchData }) => {
  const { t, i18n } = useTranslation();
  const isBelowLg = useMediaQuery(theme.breakpoints.down("lg"));
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = isBelowMd ? 5 : 10;

  const handleNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };
  const getDateLocale = () => {
    switch (i18n.language) {
      case "zh_cn":
        return zhCN;
      case "zh_hk":
        return zhHK;
      default:
        return undefined;
    }
  };

  const checkProjectIds = (activities) => {
    // 提取所有非null的project_id
    const nonNullProjectIds = activities
      .filter((activity) => activity.project_id !== null)
      .map((activity) => activity.project_id);

    // 如果没有非null的project_id，或者所有非null的project_id都相同，则返回true
    return nonNullProjectIds.every((id) => id === nonNullProjectIds[0]);
  };

  const IsActivitiesNullOrSameProject = checkProjectIds(activities);

  const columns = IsActivitiesNullOrSameProject
    ? [
        { label: t("dashboard.activity_timestamp"), accessor: "recorded_at" },
        { label: t("dashboard.activity_content"), accessor: "activity_text" },
        { label: t("dashboard.activity_type"), accessor: "type" },
        { label: t("dashboard.activity_status"), accessor: "code" },
      ]
    : [
        { label: t("dashboard.activity_timestamp"), accessor: "recorded_at" },
        { label: t("dashboard.activity_content"), accessor: "activity_text" },
        { label: t("dashboard.activity_type"), accessor: "type" },
        { label: t("dashboard.activity_relatedproject"), accessor: "project_alias" },
        { label: t("dashboard.activity_status"), accessor: "code" },
      ];
  const handleProjectButtonOnClick = (project) => {
    setProjectState({
      selectedProject: project,
    });
    setState({
      pageTabStack: [...state.pageTabStack, state.pageTab],
      pageTab: "projects",
      pageIdxStack: [...state.pageIdxStack, state.pageIdx],
      pageIdx: state.pageIdx + 1,
    });
  };
  
  const getActivityCodeStyle = (code) => {
    if (code >= 400) {
      return { color: "red" };
    } else if (code >= 200) {
      return { color: "green" };
    }
    return {};
  };

  const getCodeAlias = (code) => {
    if (code >= 400) {
      return t("dashboard.activity_failure");
    } else if (code >= 200) {
      return t("dashboard.activity_success");
    }
    return t("dashboard.activity_test");
  };

  const [sortedList, handleSort, SortFieldIcon] = useSort(activities, "recorded_at", "desc");
  useEffect(() => {
    fetchData();
  }, []);

  const totalPages = Math.ceil(sortedList.length / rowsPerPage);
  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = sortedList.slice(startIndex, endIndex);
  const tableHeight = useMapHeight();

  return (
    <ThemeProvider theme={theme}>
      <div>
        <TableContainer
          component={Paper}
          sx={{
            height: isBelowLg ? null : tableHeight,
            overflowY: "auto",
            "& thead th": {
              position: "sticky",
              top: 0,
              backgroundColor: "white",
              zIndex: 1,
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell key={`${column.accessor}_${index}`} sx={getCellSx()}>
                    <IconButton
                      onClick={() => handleSort(column.accessor)}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        borderRadius: "4px",
                        padding: "8px",
                        gap: 1,
                      }}
                    >
                      <SortFieldIcon field={column.accessor} />
                      <Typography variant={"body1"}>{column.label}</Typography>
                    </IconButton>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody style={{ height: isBelowLg ? null : "385px", overflowY: "scroll" }}>
              {currentRows.map((activity, index) => (
                <TableRow key={`${activity.sn}_${index}`}>
                  <TableCell sx={{ ...getCellSx("recorded_at"), paddingLeft: "1em" }}>
                    <Tooltip title={new Date(activity.recorded_at).toLocaleString("zh-CN")}>
                      <span>
                        {formatDistanceToNow(new Date(activity.recorded_at), {
                          addSuffix: true,
                          locale: getDateLocale(),
                        })}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={getCellSx()}>{truncateText(activity.activity_text, 6)}</TableCell>
                  <TableCell sx={getCellSx("type")}>{t(`dashboard.activity_type_${activity.type}`)}</TableCell>
                  {!IsActivitiesNullOrSameProject && (
                    <TableCell sx={getCellSx()}>
                      {truncateText(activity.project_alias)}
                      <LinkToProjectButton
                        id={activity.project_id}
                        t={t}
                        handleButtonOnClick={() =>
                          handleProjectButtonOnClick({
                            id: activity.project_id,
                            alias: activity.project_alias,
                          })
                        }
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ ...getCellSx("code"), ...getActivityCodeStyle(activity.code) }}>
                    {getCodeAlias(activity.code)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {isBelowLg && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingTop: "1em",
            }}
          >
            <IconButton onClick={handlePrevPage} disabled={currentPage === 0}>
              <KeyboardArrowLeft />
            </IconButton>
            <Typography sx={{ mx: 2 }}>
              {currentPage + 1} / {totalPages}
            </Typography>
            <IconButton onClick={handleNextPage} disabled={currentPage === totalPages - 1}>
              <KeyboardArrowRight />
            </IconButton>
          </Box>
        )}
      </div>
    </ThemeProvider>
  );
};

export default ActivityListComponent;
